import { useCallback, useEffect, useRef, useState } from 'react';

import { getToken } from '@totem/components/userContext/accountUtilities';
import { Job, JobStatus } from '@totem/types/job';
import { JOBS_ENDPOINT } from '@totem/utilities/endpoints';

const POLL_INTERVAL_MS = 2000;

type Status = 'idle' | JobStatus;

interface UseAsyncJobResult<TResult> {
  status: Status;
  result: TResult | null;
  error: string | null;
  start: () => void;
}

// Triggers a long-running backend operation that returns a jobId immediately,
// then polls /jobs/{jobId} until it completes or fails. Any endpoint that
// responds to a GET with { jobId } (see Auth0RolesController.RefreshAllRoles
// for the reference implementation) can use this — it isn't Auth0-specific.
export const useAsyncJob = <TResult>(
  triggerEndpoint: string,
): UseAsyncJobResult<TResult> => {
  const [status, setStatus] = useState<Status>('idle');
  const [result, setResult] = useState<TResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPoll = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);

      pollTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => clearPoll, [clearPoll]);

  const poll = useCallback((jobId: string) => {
    fetch(`${JOBS_ENDPOINT}/${jobId}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => res.json())
      .then((job: Job<TResult>) => {
        setStatus(job.status);

        if (job.status === 'completed' || job.status === 'failed') {
          setResult(job.result ?? null);

          setError(job.error ?? null);

          return;
        }

        pollTimeoutRef.current = setTimeout(
          () => poll(jobId),
          POLL_INTERVAL_MS,
        );
      })
      .catch(() => {
        setStatus('failed');

        setError('Failed to check job status.');
      });
  }, []);

  const start = useCallback(() => {
    clearPoll();

    setStatus('pending');

    setResult(null);

    setError(null);

    fetch(triggerEndpoint, {
      method: 'GET',
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => res.json())
      .then((response: { jobId: string }) => {
        poll(response.jobId);
      })
      .catch(() => {
        setStatus('failed');

        setError('Failed to start job.');
      });
  }, [triggerEndpoint, poll, clearPoll]);

  return { status, result, error, start };
};
